import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ShieldCheck, HeartHandshake, Users } from 'lucide-react';
import { heroImage, issaBadgeSquare } from '../../data/mock';
import ReviewsBadge from '../ReviewsBadge';
import FindBestFitButton from '../FindBestFitButton';
import { useQuiz } from '../../context/QuizProvider';

export default function Hero() {
  const { open: openQuiz } = useQuiz();
  return (
    <section className="relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 pt-12 md:pt-14 pb-16 grid lg:grid-cols-2 gap-12 items-center">
        <div className="fade-up order-2 lg:order-1">
          <ReviewsBadge />
          <h1 className="mt-5 font-display font-bold text-[3.25rem] md:text-[4.5rem] leading-[1.02] text-splash-green">
            Confidence <br /> <span className="text-splash-teal">Starts Here.</span>
          </h1>
          <p className="mt-6 text-lg text-splash-green/80 max-w-xl leading-relaxed">
            Empowering children with lifesaving skills and foundational swimming techniques in a supportive, small-group environment at Little Splash Swim.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-4">
            <Link to="/programs" data-testid="hero-get-started-btn" className="inline-flex items-center gap-2 rounded-full bg-splash-coral hover:bg-splash-coralDark text-white px-7 py-3.5 font-semibold btn-transition btn-focus">
              Get Started <ArrowRight className="w-4 h-4" />
            </Link>
            <FindBestFitButton variant="secondary" onClick={openQuiz} dataTestId="hero-best-fit-btn" />
          </div>
          <div className="mt-10 grid grid-cols-3 gap-4 max-w-xl">
            {[
              { icon: Users, label: 'Max 4 / Class' },
              { icon: ShieldCheck, label: 'WSI Certified' },
              { icon: HeartHandshake, label: 'Confidence-First' },
            ].map(({ icon: Icon, label }) => (
              <div key={label} className="flex flex-col items-start gap-2 p-4 bg-white rounded-2xl border border-splash-sand">
                <Icon className="w-5 h-5 text-splash-teal" />
                <span className="text-sm font-semibold text-splash-green">{label}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="relative fade-up order-1 lg:order-2">
          <div className="absolute -inset-6 rounded-[2.5rem] bg-splash-teal/20 rotate-2" />
          <div className="relative rounded-[2rem] shadow-2xl w-full h-[320px] md:h-[460px] lg:h-[520px] overflow-hidden">
            <img
              src={heroImage}
              alt="Instructor smiling with a child during a Little Splash swim lesson"
              fetchpriority="high"
              decoding="async"
              data-testid="hero-image"
              className="absolute inset-0 w-full h-full object-cover"
              style={{ objectPosition: '60% 100%', transform: 'scale(1.75)', transformOrigin: '62% 52%' }}
            />
          </div>
          <div className="absolute top-3 -left-3 md:top-16 md:-left-6 bg-white rounded-2xl p-2.5 md:p-4 shadow-xl border border-splash-sand max-w-[150px] md:max-w-[200px]" data-testid="hero-lessons-badge">
            <div className="text-xl md:text-3xl font-display font-bold text-splash-green">1,200+</div>
            <div className="text-[10px] md:text-xs text-splash-green/70 leading-tight">Lessons taught by our certified coaches</div>
          </div>
          <div className="absolute -bottom-5 -right-5 bg-white rounded-2xl p-3 shadow-xl border border-splash-sand w-[108px] h-[108px] grid place-items-center">
            <img src={issaBadgeSquare} alt="ISSA Accredited Swim School" loading="lazy" decoding="async" className="w-full h-full object-contain" />
          </div>
        </div>
      </div>
    </section>
  );
}
